import React, { useRef } from 'react'
import { Alert, Badge, Button, Card, CardBody, Col, Input, InputGroup, InputGroupAddon, Row } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { faInfoCircle, faUserPlus } from '@fortawesome/free-solid-svg-icons'
import Flex from '../../common/Flex'
import Loader from '../../common/Loader'
import useSWRFetch from '../../../helpers/hooks/useSWRFetch'
import { getRewardsReferrals } from '../../../helpers/api/rewards'
import {
  buildReferralLink,
  buildReferralWhatsAppUrl,
  formatNumber,
  getReferralRules,
  getReferralStatus,
  maskEmail
} from '../../rewards/rewardsUtils'
import useCopy from '../../rewards/useCopy'
import CopyButton from '../../rewards/components/CopyButton'

const ReferralRow = ({ referral, hc }) => {
  const status = getReferralStatus(referral.status)
  const awarded = Number(referral.hc_awarded) || 0

  return (
    <div className="border-bottom border-200 py-3">
      <Flex justify="between" align="start" className="flex-wrap">
        <span className="font-weight-semi-bold text-900 mr-2 text-break">{maskEmail(referral.email)}</span>
        <div className="text-nowrap">
          {referral.status === 'rewarded' && (
            <span className="fs--1 font-weight-semi-bold text-success mr-2">+{formatNumber(awarded || hc)} HC</span>
          )}
          <Badge color={`soft-${status.color}`} pill className="fs--2">
            {status.label}
          </Badge>
        </div>
      </Flex>
      <p className="fs--1 text-600 mb-0 mt-1">{status.description(referral)}</p>
    </div>
  )
}

// HO-TASK-286: bloque de referidos en el perfil del propietario. El código es
// de la propiedad seleccionada (su wallet de Rewards), por eso solo se muestra
// con una propiedad activa (ver MyProfile).
const ProfileReferrals = () => {
  const { data, error, isLoading, mutate } = useSWRFetch('rewards-referrals', getRewardsReferrals)
  const { copied, copy } = useCopy()
  const codeInput = useRef(null)
  const linkInput = useRef(null)

  const referralData = data?.data
  const code = referralData?.referral_code
  const referrals = referralData?.referrals || []
  const rules = getReferralRules(referralData)
  const link = code ? buildReferralLink(code) : ''
  const earnedHc = referrals
    .filter((referral) => referral.status === 'rewarded')
    .reduce((total, referral) => total + (Number(referral.hc_awarded) || 0), 0)
  const rewardedThisMonth = Number(referralData?.rewarded_this_month) || 0

  const renderBody = () => {
    if (isLoading) return <Loader padding="py-4" />

    if (error || !code) {
      return (
        <Alert color="warning" className="mb-0 fs--1">
          No pudimos cargar tu código de referido.
          <Button color="link" size="sm" className="ml-1 p-0 align-baseline fs--1" onClick={() => mutate()}>
            Reintentar
          </Button>
        </Alert>
      )
    }

    const shareOnWhatsApp = (
      <Button
        tag="a"
        href={buildReferralWhatsAppUrl(code)}
        target="_blank"
        rel="noopener noreferrer"
        color="success"
        size="sm"
        className="text-nowrap"
      >
        <FontAwesomeIcon icon={faWhatsapp} className="mr-1" />
        Compartir por WhatsApp
      </Button>
    )

    return (
      <>
        <Row>
          <Col md={5} className="mb-3">
            <h6 className="text-600 fs--1 mb-1">Tu código</h6>
            <Flex align="center">
              <span className="fs-2 font-weight-bold text-900 mr-3" style={{ letterSpacing: '0.12em' }}>
                {code}
              </span>
              <CopyButton
                copied={copied === 'code'}
                onClick={() => copy('code', code, codeInput.current, 'Código copiado al portapapeles.')}
              >
                Copiar
              </CopyButton>
            </Flex>
            {/* Respaldo para copiar sin navigator.clipboard. */}
            <input ref={codeInput} value={code} readOnly aria-hidden="true" tabIndex={-1} className="sr-only" />
          </Col>
          <Col md={7} className="mb-3">
            <h6 className="text-600 fs--1 mb-1">Tu enlace de registro</h6>
            <InputGroup size="sm">
              <Input innerRef={linkInput} value={link} readOnly onFocus={(event) => event.target.select()} aria-label="Enlace de referido" />
              <InputGroupAddon addonType="append">
                <CopyButton
                  copied={copied === 'link'}
                  onClick={() => copy('link', link, linkInput.current, 'Enlace copiado al portapapeles.')}
                >
                  Copiar<span className="d-none d-sm-inline"> enlace</span>
                </CopyButton>
              </InputGroupAddon>
            </InputGroup>
          </Col>
        </Row>

        <Flex align="center" className="flex-wrap mb-3">
          <div className="mr-3 mb-2">{shareOnWhatsApp}</div>
          <span className="fs--1 text-600 mb-2">
            Ganado con referidos: <b className="text-900">{formatNumber(earnedHc)} HC</b>
            {rules.monthlyCap > 0 && (
              <>
                {' · '}Premiados este mes: <b className="text-900">{rewardedThisMonth} de {rules.monthlyCap}</b>
              </>
            )}
          </span>
        </Flex>

        <Alert color="info" className="fs--1 mb-3">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
          <b>¿Cuándo recibo mis Coins?</b> No al registrarse. Los recibes cuando el condominio que invitaste tiene su{' '}
          <b>primer pago de cuota aprobado</b> en Homelan.
          {rules.monthlyCap > 0 && <> Puedes recibir el premio de hasta {rules.monthlyCap} referidos por mes.</>}
        </Alert>

        <h6 className="mb-0">Tus referidos</h6>
        {referrals.length === 0 ? (
          <div className="text-center py-4">
            <FontAwesomeIcon icon={faUserPlus} className="fs-3 text-300 mb-2" />
            <p className="fs--1 text-600 mb-3">
              Aún no has invitado a nadie. Comparte tu código con el administrador o la junta de otro condominio.
            </p>
            {shareOnWhatsApp}
          </div>
        ) : (
          referrals.map((referral) => <ReferralRow key={referral.id} referral={referral} hc={rules.hc} />)
        )}
      </>
    )
  }

  return (
    <Card className="mb-3">
      <CardBody>
        <Flex align="center" className="mb-3">
          <div
            className="d-none d-sm-flex flex-center rounded-circle mr-3 flex-shrink-0 bg-soft-info text-info"
            style={{ width: '3rem', height: '3rem' }}
          >
            <FontAwesomeIcon icon={faUserPlus} className="fs-2" />
          </div>
          <div>
            <h5 className="mb-1">Invita y gana Homelan Coins</h5>
            <p className="fs--1 text-600 mb-0">
              ¿Conoces un condominio que aún no usa Homelan? Invita a su administrador o a su junta y gana{' '}
              <b className="text-900">{formatNumber(rules.hc)} HC</b>
              {rules.tickets > 0 && <> y {rules.tickets} tickets para el sorteo del mes</>}.
            </p>
          </div>
        </Flex>
        {renderBody()}
      </CardBody>
    </Card>
  )
}

export default ProfileReferrals
